// app/components/SessionModal.tsx - Updated interface
import { Portal, Modal, Text, Button, TextInput, HelperText, useTheme, RadioButton, Surface } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn, SubmitHandler } from 'react-hook-form';
import { OdometerFormData } from '../lib/types';
import { Platform, ScrollView, View, KeyboardAvoidingView, Keyboard, Dimensions, useWindowDimensions } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { geocodeAddressToPointWKT } from '../api/utils/geocoding';
import { track } from '../lib/analytics';
import { isLocationUnavailableError } from '../utils/locationUtils';

interface Vehicle {
  id: number;
  registration_number: string;
  make?: string;
  model?: string;
  year?: number;
  fuel_type?: string;
}

interface SessionModalProps {
  visible: boolean;
  onDismiss: () => void;
  form: UseFormReturn<OdometerFormData>;
  onSubmit: SubmitHandler<OdometerFormData>;
  onSubmitWithLocation?: (data: OdometerFormData, locationWkt: string) => Promise<boolean> | boolean;
  selectedVehicle: number | null;
  vehicles?: Vehicle[]; // Optional: if provided and length > 1, show vehicle selection
  onSelectVehicle?: (vehicleId: number) => void; // Callback when vehicle is selected
  allowVehicleSelection?: boolean; // Disable for end-session to keep session vehicle fixed
  title: string;
  buttonText: string;
  isLoading: boolean;
  latestOdometer: number | null;
  retryLocationPermission?: () => void;
}

export const SessionModal = ({
  visible,
  onDismiss,
  form,
  onSubmit,
  onSubmitWithLocation,
  selectedVehicle,
  vehicles = [],
  onSelectVehicle,
  allowVehicleSelection = true,
  title,
  buttonText,
  isLoading,
  latestOdometer,
  retryLocationPermission,
}: SessionModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const { control, handleSubmit, formState: { errors }, reset } = form;
  const [manualLocationText, setManualLocationText] = useState('');
  const [manualLocationError, setManualLocationError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const keyboardVisibleRef = useRef(false);
  const windowHeightRef = useRef(Dimensions.get('window').height);
  const lastResizeAtRef = useRef<number>(Date.now());
  const jankEventsCountRef = useRef(0);

  useEffect(() => {
    if (!visible) return;

    const modalName = title === 'endSession' ? 'endSession' : 'startSession';
    jankEventsCountRef.current = 0;
    keyboardVisibleRef.current = false;
    setIsKeyboardVisible(false);
    setIsInputFocused(false);
    windowHeightRef.current = Dimensions.get('window').height;
    lastResizeAtRef.current = Date.now();

    void track('ui_modal_open', {
      screen: 'dashboard_home',
      modal: modalName,
      platform: Platform.OS,
      osVersion: String(Platform.Version),
    });

    const keyboardShowEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const keyboardHideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const onKeyboardShow = Platform.OS === 'web'
      ? null
      : Keyboard.addListener(keyboardShowEvent, (e) => {
          keyboardVisibleRef.current = true;
          setIsKeyboardVisible(true);
          void track('ui_keyboard_show', {
            screen: 'dashboard_home',
            modal: modalName,
            platform: Platform.OS,
            osVersion: String(Platform.Version),
            keyboardHeight: e?.endCoordinates?.height ?? null,
          });
        });

    const onKeyboardHide = Platform.OS === 'web'
      ? null
      : Keyboard.addListener(keyboardHideEvent, () => {
          keyboardVisibleRef.current = false;
          setIsKeyboardVisible(false);
          void track('ui_keyboard_hide', {
            screen: 'dashboard_home',
            modal: modalName,
            platform: Platform.OS,
            osVersion: String(Platform.Version),
          });
        });

    const onDimensionChange = Dimensions.addEventListener('change', ({ window }) => {
      const now = Date.now();
      const prevHeight = windowHeightRef.current;
      const nextHeight = window.height;
      const delta = Math.abs(nextHeight - prevHeight);
      const elapsedMs = now - lastResizeAtRef.current;

      if (keyboardVisibleRef.current && delta >= 24 && elapsedMs <= 350 && jankEventsCountRef.current < 2) {
        jankEventsCountRef.current += 1;
        void track('ui_keyboard_modal_jank', {
          screen: 'dashboard_home',
          modal: modalName,
          platform: Platform.OS,
          osVersion: String(Platform.Version),
          keyboardVisible: true,
          windowHeightBefore: prevHeight,
          windowHeightAfter: nextHeight,
          heightDelta: delta,
          elapsedMs,
          flickerScore: Math.min(100, Math.round(delta * 1.5)),
        });
      }

      windowHeightRef.current = nextHeight;
      lastResizeAtRef.current = now;
    });

    const visualViewport = Platform.OS === 'web' ? window.visualViewport : null;
    const initialViewportHeight = visualViewport?.height ?? null;

    const handleViewportResize = () => {
      if (!visualViewport || initialViewportHeight === null) return;
      const delta = initialViewportHeight - visualViewport.height;
      const keyboardNowVisible = delta > 120;
      keyboardVisibleRef.current = keyboardNowVisible;
      setIsKeyboardVisible(keyboardNowVisible);
    };

    if (visualViewport) {
      visualViewport.addEventListener('resize', handleViewportResize);
      visualViewport.addEventListener('scroll', handleViewportResize);
    }

    return () => {
      setIsKeyboardVisible(false);
      setIsInputFocused(false);
      onKeyboardShow?.remove();
      onKeyboardHide?.remove();
      onDimensionChange.remove();
      if (visualViewport) {
        visualViewport.removeEventListener('resize', handleViewportResize);
        visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, [visible, title]);

  const handleDismiss = () => {
    void track('ui_modal_close', {
      screen: 'dashboard_home',
      modal: title === 'endSession' ? 'endSession' : 'startSession',
      platform: Platform.OS,
      osVersion: String(Platform.Version),
    });
    keyboardVisibleRef.current = false;
    setIsKeyboardVisible(false);
    setIsInputFocused(false);
    reset();
    form.clearErrors();
    setManualLocationText('');
    setManualLocationError(null);
    onDismiss();
  };
  
  // Show vehicle selection only when explicitly allowed (start-session)
  const showVehicleSelection = allowVehicleSelection && vehicles && vehicles.length > 1;
  const keyboardActive = isKeyboardVisible || isInputFocused;

  // Always enable scroll on small screens or when keyboard/vehicle selection is active.
  // On 320x693 (iPhone SE) the end-session modal overflows the viewport when the
  // observations field + location fallback are both present, so scroll must be on.
  const isSmallViewport = windowHeight <= 700;
  const shouldEnableScroll = keyboardActive || showVehicleSelection || isSmallViewport;

  // Single-button flow: try automatic location first; if denied and an address is provided, geocode and retry with provided location
  const handleFormSubmit = async (data: OdometerFormData) => {
    try {
      const result: any = await onSubmit(data);
      if (result === true) {
        handleDismiss();
        return;
      }
      if (result === 'location_denied') {
        // Require manual address if location is denied
        if (!manualLocationText.trim()) {
          setManualLocationError(t('manualLocationPrompt'));
          return;
        }
        // Clear error on manual path attempt
        form.clearErrors('odometer');
        setIsGeocoding(true);
        setManualLocationError(null);
        try {
          const geo = await geocodeAddressToPointWKT(manualLocationText.trim());
          if (!geo) {
            setManualLocationError(t('locationNotFound'));
            return;
          }
          if (onSubmitWithLocation) {
            const ok = await onSubmitWithLocation(data, geo.wkt);
            if (ok) {
              handleDismiss();
              return;
            }
          }
        } finally {
          setIsGeocoding(false);
        }
        return;
      }
    } catch (error) {
      console.error(`Erro ao ${title.toLowerCase()}:`, error);
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleDismiss}
        testID="modal-wrapper"
        contentContainerStyle={[
          {
            backgroundColor: theme.colors.surface,
            padding: 16,
            marginHorizontal: 16,
            marginVertical: keyboardActive ? 8 : 16,
            borderRadius: 8,
            maxHeight: isSmallViewport
              ? (keyboardActive ? windowHeight * 0.95 : windowHeight * 0.92)
              : Math.min(windowHeight - 32, keyboardActive ? windowHeight * 0.92 : windowHeight * 0.9),
          },
          Platform.OS === 'web' ? {
            maxWidth: 500,
            width: '90%',
            alignSelf: 'center',
          } : {}
        ]}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 24 : 0}
          style={{ maxHeight: '100%' }}
        >
          <ScrollView
            scrollEnabled={shouldEnableScroll}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            showsVerticalScrollIndicator={shouldEnableScroll}
            bounces={shouldEnableScroll}
            contentContainerStyle={{ paddingBottom: keyboardActive ? 12 : 0 }}
          >
            <Text variant="headlineSmall" style={{ marginBottom: 16 }}>
              {t(title)}
            </Text>
            
            {/* Vehicle Selection Section - Show if multiple vehicles */}
            {showVehicleSelection && (
              <View style={{ marginBottom: 16 }}>
                <Text variant="titleMedium" style={{ marginBottom: 10, fontWeight: 'bold' }}>
                  {t('vehicleSelection.title')}
                </Text>
                <ScrollView style={{ maxHeight: Math.min(180, windowHeight * 0.25), marginBottom: 8 }}>
                  <RadioButton.Group 
                    onValueChange={(value) => onSelectVehicle?.(parseInt(value))}
                    value={selectedVehicle?.toString() || ''}
                  >
                    {vehicles.map((vehicle) => (
                      <Surface
                        key={vehicle.id}
                        style={{
                          marginBottom: 8,
                          borderRadius: 8,
                          backgroundColor: selectedVehicle === vehicle.id 
                            ? theme.colors.primaryContainer 
                            : theme.colors.surfaceVariant,
                        }}
                      >
                        <RadioButton.Item
                          label={[
                            vehicle.registration_number,
                            [vehicle.make, vehicle.model].filter(Boolean).join(' ')
                          ].filter(Boolean).join(' · ')}
                          value={vehicle.id.toString()}
                          status={selectedVehicle === vehicle.id ? 'checked' : 'unchecked'}
                          style={{
                            paddingVertical: 4,
                          }}
                        />
                      </Surface>
                    ))}
                  </RadioButton.Group>
                </ScrollView>
              </View>
            )}
            
            {latestOdometer !== null && (
              <Text style={{ marginBottom: 8 }}>
                {t('lastOdometerReading')}: {latestOdometer} km
              </Text>
            )}
            <Controller
              control={control}
              name="odometer"
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                const message = error?.message || '';
                const isLocationDenied =
                  isLocationUnavailableError(message) ||
                  message.toLowerCase().includes('location permission');
                const showManualBlock = isLocationDenied || manualLocationText.trim().length > 0;
                const isLocationSettingsInvalid = message === 'LOCATION_SETTINGS_INVALID';
                return (
                  <>
                    <TextInput
                      label={t('odometer')}
                      mode="outlined"
                      onChangeText={(text) => {
                        // Keep the field editable while the user clears/types.
                        // Avoid forcing 0 when empty (can cause focus/keyboard jitter on Android).
                        const digitsOnly = text.replace(/[^0-9]/g, '');
                        if (digitsOnly.length === 0) {
                          onChange(undefined);
                          return;
                        }
                        onChange(parseInt(digitsOnly, 10));
                      }}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      value={value === undefined || value === null ? '' : String(value)}
                      error={!!error}
                      keyboardType="numeric"
                      style={{ marginBottom: 14 }}
                      disabled={isLoading}
                    />
                    {error && !isLocationDenied && (
                      <Text style={{ color: theme.colors.error, marginBottom: 8 }}>{message}</Text>
                    )}
                    {showManualBlock && (
                      <>
                        {isLocationDenied && !manualLocationText.trim() && (
                          <Text style={{ color: theme.colors.error, marginBottom: 8 }}>
                            {isLocationSettingsInvalid
                              ? t('alerts.locationSettingsManualFallback')
                              : t('locationRequiredGeneric')}
                          </Text>
                        )}
                        <Text style={{ marginBottom: 6 }}>
                          {t('manualLocationPrompt')}
                        </Text>
                        <TextInput
                          label={t('manualLocationLabel')}
                          mode="outlined"
                          value={manualLocationText}
                          onChangeText={(text) => { setManualLocationText(text); setManualLocationError(null); }}
                          onFocus={() => setIsInputFocused(true)}
                          onBlur={() => setIsInputFocused(false)}
                          style={{ marginBottom: 6 }}
                          disabled={isLoading || isGeocoding}
                        />
                        {!!manualLocationError && (
                          <HelperText type="error" visible={true} style={{ marginBottom: 6 }}>
                            {manualLocationError}
                          </HelperText>
                        )}
                      </>
                    )}
                  </>
                );
              }}
            />
            {title === 'endSession' && (
              <Controller
                control={control}
                name="observations"
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    label={t('observations')}
                    mode="outlined"
                    onChangeText={onChange}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    value={value || ''}
                    multiline
                    numberOfLines={2}
                    contentStyle={{ textAlignVertical: 'top' }}
                    style={{ marginBottom: 14, minHeight: 76, maxHeight: 104 }}
                    disabled={isLoading}
                  />
                )}
              />
            )}
            <Button
              mode="contained"
              onPress={handleSubmit(handleFormSubmit)}
              loading={isLoading || isGeocoding}
              disabled={isLoading || isGeocoding || !selectedVehicle}
              style={{ marginBottom: 6 }}
            >
              {t(buttonText)}
            </Button>
            <Button
              mode="text"
              onPress={handleDismiss}
              disabled={isLoading}
              style={{ marginTop: 6 }}
            >
              {t('cancel')}
            </Button>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
    </Portal>
  );
};
